import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save, Lock } from "lucide-react";

/* -------------------- presets -------------------- */
const PRESETS = [
  { key: "balanced", label: "Balanced",  p: 30, c: 40, f: 30 },
  { key: "cut",      label: "Cut",       p: 40, c: 30, f: 30 },
  { key: "bulk",     label: "Bulk",      p: 25, c: 50, f: 25 },
  { key: "keto",     label: "Keto",      p: 25, c: 10, f: 65 },
  { key: "endur",    label: "Endurance", p: 20, c: 60, f: 20 },
];

const COLORS = {
  protein: "#6B9BFF",
  carbs:   "#FFB020",
  fat:     "#7C3AED",
};

const round1 = (x) => Math.round(x * 10) / 10;

/** Adjust the changed channel + redistribute the remaining 100 - new across the other two
 *  proportionally to their previous values. Locks the locked channel exactly.
 */
function rebalance(prev, changedKey, newValue, lockedKey = null) {
  const v = Math.max(0, Math.min(100, newValue));
  const out = { ...prev, [changedKey]: v };
  // Identify the two "other" channels
  const others = ["protein", "carbs", "fat"].filter((k) => k !== changedKey);
  // If one is locked, the third absorbs everything
  const lockedOther = lockedKey && others.includes(lockedKey) ? lockedKey : null;
  const free = others.filter((k) => k !== lockedOther);
  const lockedVal = lockedOther ? prev[lockedOther] : 0;
  const budget = Math.max(0, 100 - v - lockedVal);
  if (lockedOther) {
    out[lockedOther] = lockedVal;
    out[free[0]] = budget;
  } else {
    // Distribute budget proportionally to previous values
    const sumOthers = others.reduce((s, k) => s + prev[k], 0) || 1;
    others.forEach((k) => {
      out[k] = round1((prev[k] / sumOthers) * budget);
    });
  }
  // Normalize to exactly 100 — pin the changed channel exactly, distribute rounding into the first free
  const sum = out.protein + out.carbs + out.fat;
  if (sum !== 100) {
    const adjustKey = lockedOther ? free[0] : others[0];
    out[adjustKey] = round1(out[adjustKey] + (100 - sum));
  }
  return out;
}

/* -------------------- component -------------------- */

/**
 * Per-user macro split editor. Drag sliders or pick a preset.
 *
 * Props:
 *   - kcalTarget: number — daily calorie target used to convert pct → grams (live preview)
 *   - onSaved: fn(profile) — optional callback after a successful PUT
 *   - compact: bool — slim variant (no big heading) for modal contexts
 */
export default function MacroSplitEditor({ kcalTarget = 2200, onSaved, compact = false }) {
  const { user, setUser } = useAuth();
  const initial = user?.macro_split || { protein_pct: 30, carbs_pct: 40, fat_pct: 30 };
  const [split, setSplit] = useState({
    protein: round1(initial.protein_pct ?? 30),
    carbs:   round1(initial.carbs_pct ?? 40),
    fat:     round1(initial.fat_pct ?? 30),
  });
  const [lockedKey, setLockedKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const usingManual = !!user?.macro_split;

  // If user.macro_split changes externally (login/refresh), sync local state.
  useEffect(() => {
    if (user?.macro_split) {
      setSplit({
        protein: round1(user.macro_split.protein_pct ?? 30),
        carbs:   round1(user.macro_split.carbs_pct ?? 40),
        fat:     round1(user.macro_split.fat_pct ?? 30),
      });
    }
  }, [user?.macro_split]);

  const update = (key, v) => setSplit((prev) => rebalance(prev, key, Number(v), lockedKey));
  const applyPreset = (p) => setSplit({ protein: p.p, carbs: p.c, fat: p.f });

  const proteinG = Math.round((kcalTarget * split.protein / 100) / 4);
  const carbsG   = Math.round((kcalTarget * split.carbs   / 100) / 4);
  const fatG     = Math.round((kcalTarget * split.fat     / 100) / 9);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        macro_split: {
          protein_pct: split.protein,
          carbs_pct: split.carbs,
          fat_pct: split.fat,
        },
      };
      const { data } = await api.put("/auth/profile", body);
      setUser(data);
      toast.success("Macro split saved");
      onSaved?.(data);
    } catch (err) {
      console.error(err);
      toast.error("Could not save macro split");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      // Explicit null clears the override (server treats unset-vs-null differently now).
      const { data } = await api.put("/auth/profile", { macro_split: null });
      setUser(data);
      setSplit({ protein: 30, carbs: 40, fat: 30 });
      toast.success("Reverted to AI-suggested macros");
      onSaved?.(data);
    } catch {
      toast.error("Could not reset");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`border border-white/10 bg-[#121212] ${compact ? "p-5" : "p-6"}`}
      data-testid="macro-split-editor"
    >
      {!compact && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-eyebrow text-[#6B9BFF] mb-1">Macro split</div>
            <div className="font-display text-2xl leading-none">
              {usingManual ? "Custom split" : "AI-suggested"}
            </div>
          </div>
          <div className="text-right">
            <div className="label-eyebrow text-white/40 text-[10px]">target</div>
            <div className="font-display text-xl text-[#6B9BFF]">{kcalTarget} kcal</div>
          </div>
        </div>
      )}

      {/* Quick presets */}
      <div className="mb-5 flex flex-wrap gap-2">
        {PRESETS.map((p) => {
          const active = split.protein === p.p && split.carbs === p.c && split.fat === p.f;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p)}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold border transition-colors ${
                active
                  ? "border-[#6B9BFF] text-[#6B9BFF] bg-[#6B9BFF]/10"
                  : "border-white/10 text-white/60 hover:border-white/30"
              }`}
              data-testid={`macro-preset-${p.key}`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Sliders */}
      <div className="space-y-4">
        {[
          { key: "protein", label: "Protein", color: COLORS.protein, kcalPerG: 4, grams: proteinG },
          { key: "carbs",   label: "Carbs",   color: COLORS.carbs,   kcalPerG: 4, grams: carbsG },
          { key: "fat",     label: "Fat",     color: COLORS.fat,     kcalPerG: 9, grams: fatG },
        ].map((m) => (
          <div key={m.key} data-testid={`macro-row-${m.key}`}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2" style={{ background: m.color }} />
                <span className="label-eyebrow text-white/70">{m.label}</span>
                <button
                  type="button"
                  onClick={() => setLockedKey((k) => (k === m.key ? null : m.key))}
                  className={`text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 ml-2 transition-colors ${
                    lockedKey === m.key
                      ? "text-[#6B9BFF]"
                      : "text-white/30 hover:text-white/70"
                  }`}
                  title={lockedKey === m.key ? "Click to unlock" : "Lock this channel"}
                  data-testid={`macro-lock-${m.key}`}
                >
                  <Lock className="w-2.5 h-2.5" /> {lockedKey === m.key ? "Locked" : "Lock"}
                </button>
              </div>
              <div className="text-right">
                <span className="font-display text-base" style={{ color: m.color }}>{round1(split[m.key])}%</span>
                <span className="text-white/40 text-xs ml-2">≈ {m.grams}g</span>
              </div>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={split[m.key]}
              onChange={(e) => update(m.key, e.target.value)}
              disabled={lockedKey === m.key}
              className="w-full accent-[#6B9BFF] disabled:opacity-50"
              data-testid={`macro-slider-${m.key}`}
              style={{ accentColor: m.color }}
            />
          </div>
        ))}
      </div>

      {/* Sanity totals row */}
      <div className="mt-5 pt-4 border-t border-white/5 grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="label-eyebrow text-white/40 text-[10px]">Total</div>
          <div className="font-display text-base text-white">
            {round1(split.protein + split.carbs + split.fat)}%
          </div>
        </div>
        <div>
          <div className="label-eyebrow text-white/40 text-[10px]">Calories</div>
          <div className="font-display text-base text-white">{kcalTarget}</div>
        </div>
        <div>
          <div className="label-eyebrow text-white/40 text-[10px]">Grams</div>
          <div className="font-display text-base text-white">
            <span style={{ color: COLORS.protein }}>{proteinG}</span>
            <span className="text-white/30 mx-1">/</span>
            <span style={{ color: COLORS.carbs }}>{carbsG}</span>
            <span className="text-white/30 mx-1">/</span>
            <span style={{ color: COLORS.fat }}>{fatG}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-5 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={reset}
          disabled={saving || !usingManual}
          className="text-[11px] uppercase tracking-widest font-bold text-white/50 hover:text-white inline-flex items-center gap-1.5 disabled:opacity-30"
          data-testid="macro-reset"
        >
          <RotateCcw className="w-3 h-3" /> Reset to AI
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="bg-[#6B9BFF] text-black font-bold uppercase tracking-widest text-xs px-5 py-2.5 hover:bg-white transition-colors inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="macro-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? "Saving…" : "Save split"}
        </button>
      </div>
    </div>
  );
}
